/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {useEffect, useMemo} from 'react';
import {useQuery} from 'react-query';
import {error} from '@ion/core/src/logger/logger';
import {searchDevices} from '@ion/core/src/services/Connect';
import {DeviceAggregationsAppIdResponseType} from '@ion/core/src/types/entities';
import {DeviceSearchTypes} from '@ion/core/src/types/services';
import {createKey, QueryKey} from '../config/QueryKeys';
import {createQueryResult, getStaleTime} from '../utils/helpers';
import {UseDefaultQueryResult} from './types';

export type UseDeviceAppIdsResult = UseDefaultQueryResult & {
  deviceAppIds: string[];
};

/**
 * The useDeviceAppIds hook provides a React Query hook for referencing device app IDs.
 *
 * ```typescript
 *
 * import {useDeviceAppIds} from '@ion/query/src/hooks/';
 *
 *  ...
 *
 *  const {deviceAppIds} = useDeviceAppIds(accountId, queryString);
 *
 * ```
 *
 * @param   accountId ID of the account within which to perform the device search.
 * @param   queryString The smart search query string to use for the device search.
 * @param   includeSubAccounts Whether to include sub-accounts in the device search.
 * @param   enabled When true, the query will be executed immediately.
 * @param   staleTime Allows for setting the staleTime for cached query data,
 *          defaults to 60 minutes (CTC session timeout duration).
 * @returns UseDeviceAppIdsResult containing the query results and the device app IDs.
 */
export const useDeviceAppIds = ({
  accountId,
  customQuery = null,
  queryString,
  includeSubAccounts,
  enabled = true,
  staleTime = getStaleTime(5, false, false)
}): UseDeviceAppIdsResult => {
  const key = useMemo(() => {
    return createKey(
      QueryKey.DEVICE_APP_IDS,
      accountId,
      customQuery,
      queryString,
      includeSubAccounts
    );
  }, [accountId, customQuery, queryString, includeSubAccounts]);

  const query = useQuery(key, async() => {
    try {
      const {data} = await searchDevices(
        DeviceSearchTypes.APP_ID,
        accountId,
        includeSubAccounts,
        queryString,
        customQuery
      );
      return parseResponse(data?.response);
    } catch (err) {
      error('useDeviceAppIds: An error occurred while fetching results:', err);
    }
  }, {staleTime: staleTime as number, enabled});

  return createQueryResult(query, {
    deviceAppIds : query.data || []
  }) as UseDeviceAppIdsResult;
};

const parseResponse = (response: DeviceAggregationsAppIdResponseType): string[] => (
  response?.aggregations?.appId?.buckets?.map(({key}) => key) || []
);

export default useDeviceAppIds;
